import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getCategoryConfig, getMediaById, getRelated } from "@/lib/media";
import { getTypeLabel } from "@/lib/labels";

export default function Detail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState("");

  const item = useMemo(() => getMediaById(id), [id]);
  const related = useMemo(() => (item ? getRelated(item, 6) : []), [item]);

  useDocumentMeta({
    title: item
      ? `${item.title} - 91视频网站详情介绍`
      : "影视详情未找到 - 91视频网站",
    description: item
      ? `${item.title}是一部${item.year}年${item.region}${item.genre}${getTypeLabel(
          item.type,
        )}，由${item.director}执导，${item.actors.slice(0, 3).join("、")}主演。${item.description.slice(
          0,
          70,
        )}`
      : "抱歉，你访问的影视详情不存在或已下线，请返回首页浏览其他内容。",
    keywords: item
      ? `${item.title},${item.title}剧情,${item.actors.slice(0, 2).join(",")},91视频网站,${item.genre}${getTypeLabel(item.type)}`
      : "91视频网站,影视资源",
    canonicalPath: `/detail/${id}`,
  });

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("链接已复制，快去分享给朋友吧");
    } catch {
      setToast("复制失败，请手动复制浏览器地址栏链接");
    }
  };

  if (!item) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-warning-line"
          title="没有找到这部影视作品"
          description="该影视资源可能已下线或链接有误，返回首页继续发现更多精彩内容吧。"
        />
      </div>
    );
  }

  const category = getCategoryConfig(item.type);

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={item.cover}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-40 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/60"></div>

        <div className="relative w-full px-4 py-8 md:px-8 md:py-12">
          <nav className="mb-5 flex flex-wrap items-center gap-1 text-sm text-background-200/80" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            {category ? (
              <>
                <Link to={category.path} className="transition-colors hover:text-accent-400 cursor-pointer">
                  {category.name}
                </Link>
                <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              </>
            ) : null}
            <span className="text-background-50">{item.title}</span>
          </nav>

          <div className="flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="w-full max-w-[220px] shrink-0">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-primary-900">
                <img
                  src={item.cover}
                  alt={`${item.title} ${item.genre}${getTypeLabel(item.type)}封面`}
                  title={`${item.title} - ${item.genre}${getTypeLabel(item.type)}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                  {getTypeLabel(item.type)}
                </span>
                <button
                  type="button"
                  onClick={() => category && navigate(category.path)}
                  className="rounded-md border border-background-50/40 px-2.5 py-1 text-xs text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                >
                  {item.genre}
                </button>
                <span className="flex items-center gap-1 rounded-md border border-background-50/40 px-2.5 py-1 text-xs text-background-50">
                  <i className="ri-star-fill text-[10px] text-accent-400" aria-hidden="true"></i>
                  {item.rating.toFixed(1)} 分
                </span>
                <span className="rounded-md border border-background-50/40 px-2.5 py-1 text-xs text-background-50">
                  {item.duration}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
                {item.title}
              </h1>

              <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-200/70">导演</dt>
                  <dd className="text-background-50">{item.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-200/70">上映年份</dt>
                  <dd className="text-background-50">{item.year} 年</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-200/70">地区</dt>
                  <dd className="text-background-50">{item.region}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="shrink-0 text-background-200/70">访问热度</dt>
                  <dd className="text-background-50">{(item.views / 10000).toFixed(1)} 万次</dd>
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-background-200/70">主演</dt>
                  <dd className="flex flex-wrap gap-x-2 gap-y-1 text-background-50">
                    {item.actors.map((actor) => (
                      <span key={actor} className="rounded bg-background-50/10 px-2 py-0.5">
                        {actor}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  返回上一页
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-2 rounded-md border border-background-50/50 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-share-forward-line" aria-hidden="true"></i>
                  分享作品
                </button>
                <Link
                  to="/"
                  className="flex items-center gap-2 rounded-md border border-background-50/50 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-4-line" aria-hidden="true"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
          <article>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              剧情简介
            </h2>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              {item.description}
            </p>
            <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
              《{item.title}》是一部来自{item.region}的{item.genre}
              {getTypeLabel(item.type)}，于 {item.year} 年推出，由{item.director}执导，
              {item.actors.slice(0, 3).join("、")}等演员联合出演。影片凭借扎实的叙事与出色的表演，
              在同类作品中脱颖而出，收获了{item.rating.toFixed(1)}分的高口碑。
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {item.tags.map((tag) => (
                <Link
                  key={tag}
                  to={category ? category.path : "/"}
                  className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
                >
                  # {tag}
                </Link>
              ))}
            </div>
          </article>

          <aside>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              作品标签
            </h2>
            <dl className="mt-4 space-y-3 rounded-lg border border-background-200 bg-background-100 p-4 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">类型</dt>
                <dd className="text-foreground-900">{getTypeLabel(item.type)}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">题材</dt>
                <dd className="text-foreground-900">{item.genre}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">地区</dt>
                <dd className="text-foreground-900">{item.region}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">评分</dt>
                <dd className="flex items-center gap-1 font-semibold text-accent-600">
                  <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                  {item.rating.toFixed(1)}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-500">更新时间</dt>
                <dd className="text-foreground-900">{item.updatedAt}</dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>

      {related.length > 0 ? (
        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-12">
          <SectionHeading
            title="相关推荐"
            subtitle="根据类型与主演为你精选"
            icon="ri-movie-2-line"
            moreLink={category ? category.path : undefined}
          />
          <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {related.map((rel) => (
              <MediaCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>
      ) : null}

      {toast ? (
        <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950/90 px-4 py-3 text-sm text-background-50">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-accent-400" aria-hidden="true"></i>
            {toast}
          </span>
        </div>
      ) : null}
    </div>
  );
}